// import utils
import {
  buildEntityConfigs,
  getEntityLabel
} from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { themeExtension } from 'config/theme.extension';

// import types
import type { EventResponse, PlanningEntityType } from 'generated/index';

const { palette } = themeExtension.custom!.constants.MRP;

/**
 * The appointment's participants, worded exactly the way an afsprakenplanning card words them when it has
 * run out of room to draw them with their icons: per type either the single name or a count and the
 * plural noun ("2 Medewerkers"), then the workplace, joined by "; ".
 *
 * Built from afsprakenplanning's own {@link buildEntityConfigs} and {@link getEntityLabel} rather than a
 * second implementation, so the same appointment reads the same in both places — including the rule that
 * the card's own entity is dropped from its list but still counted, since it is on the appointment even
 * though the card already sits under it.
 *
 * Names are trimmed: some stored names carry a trailing space, which would otherwise land straight in
 * front of the separator that follows.
 */
export const getParticipantSummary = (
  event: EventResponse,
  cardEntityId: string,
  entityType: PlanningEntityType
): string => {
  const isCardEntity = (type: PlanningEntityType, id?: string) =>
    entityType === type && !!id && id === cardEntityId;

  const entityConfigs = buildEntityConfigs({
    palette,
    otherEmployees: (event.employees ?? []).filter(
      (employee) => !isCardEntity('EMPLOYEE', employee.entityId)
    ),
    otherClients: (event.clients ?? []).filter(
      (client) => !isCardEntity('CLIENT', client.id)
    ),
    otherResources: (event.resources ?? []).filter(
      (resource) => !isCardEntity('RESOURCE', resource.id)
    ),
    isEmployee: entityType === 'EMPLOYEE',
    isClient: entityType === 'CLIENT',
    isResource: entityType === 'RESOURCE'
  });

  const workPlaceName = isCardEntity('WORKPLACE', event.workPlace?.entityId)
    ? undefined
    : event.workPlace?.name;

  return [...entityConfigs.map(getEntityLabel), workPlaceName]
    .map((label) => (label ?? '').trim())
    .filter(Boolean)
    .join('; ');
};
